import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import RequestJourney from './RequestJourney';
import { INVITE_STATE, getInviteState, inviteDaysLeft, inviteExpiredAgo } from '../utils/inviteState';

const RequestCard = ({ request, journey, isAdmin, isCoordinator, onViewDetails, onApprove, onReject, onInviteVendor, onContinueDraft, getStatusColor, getStatusIcon }) => {
  // Derived from the invite's real expiry, not the sticky `vendor_invited`
  // flag, so a lapsed link stops claiming the vendor is still on the hook.
  const inviteState = getInviteState(request);
  const daysLeft = inviteDaysLeft(request);
  const expiredAgo = inviteExpiredAgo(request);

  const InviteExpiredBadge = () => (
    <div className="px-3 py-1.5 bg-red-50 border border-red-300 rounded-lg text-sm text-red-700 flex items-center gap-2">
      <Icon name="Clock" size={16} />
      <span>Invite expired{expiredAgo ? ` — ${expiredAgo}` : ''}</span>
    </div>
  );

  const InvitedBadge = () => (
    <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-300 rounded-lg text-sm text-emerald-700 flex items-center gap-2">
      <Icon name="CheckCircle" size={16} />
      <span>Vendor Invited{daysLeft != null ? ` — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : ''}</span>
    </div>
  );

  const RegisteredBadge = () => (
    <div className="px-3 py-1.5 bg-green-50 border border-green-300 rounded-lg text-sm text-green-700 flex items-center gap-2">
      <Icon name="UserCheck" size={16} />
      Vendor Registered
    </div>
  );

  const getStatusBarColor = (status) => {
    switch (status) {
      case 'draft': return 'from-slate-400 via-gray-500 to-gray-600';
      case 'submitted': return 'from-blue-500 via-indigo-500 to-indigo-600';
      case 'approved': return 'from-emerald-400 via-green-500 to-teal-500';
      case 'rejected': return 'from-red-500 via-red-600 to-orange-600';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gradient-to-r from-slate-500 to-gray-600 text-white';
      case 'submitted': return 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white';
      case 'approved': return 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white';
      case 'rejected': return 'bg-gradient-to-r from-red-500 to-orange-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysAgo = (date) => {
    const today = new Date();
    const submissionDate = new Date(date);
    const diffTime = Math.abs(today - submissionDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all duration-200">
      {/* Status indicator bar with gradient */}
      <div className={`h-2 bg-gradient-to-r ${getStatusBarColor(request.status)}`} />
      
      <div className="p-6">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-bold text-slate-900">{request.required_vendor_name}</h3>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <Icon name="Calendar" size={16} />
                <span>Created {formatDate(request.created_at)}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="Clock" size={16} />
                <span>{getDaysAgo(request.created_at)} days ago</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusBadgeColor(request.status)}`}>
              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            </span>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent my-4" />

        {/* Downstream onboarding journey (invite -> vendor -> docs -> approval -> code) */}
        {journey && <RequestJourney journey={journey} requestStatus={request.status} />}

        {/* Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <Icon name="Phone" size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Contact</p>
              <p className="text-sm font-bold text-slate-900 truncate">{request.vendor_contact}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <Icon name="Mail" size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Email</p>
              <p className="text-sm font-bold text-slate-900 truncate">{request.vendor_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <Icon name="User" size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Requested By</p>
              <p className="text-sm font-bold text-slate-900 truncate">{request.requested_by}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-sm flex-shrink-0">
              <Icon name="Building2" size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">Site/Plant</p>
              <p className="text-sm font-bold text-slate-900 truncate">{request.user_site_plant}</p>
            </div>
          </div>
        </div>

        {/* Submitted by */}
        {request.user && (
          <div className="flex items-center gap-2 mb-4 text-sm text-slate-600">
            <Icon name="UserCircle" size={16} />
            <span>Submitted by: {request.user.full_name} ({request.user.email})</span>
          </div>
        )}

        <div className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent my-4" />

        {/* Actions */}
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(request)}
            iconName="Eye"
            iconPosition="left"
          >
            View Details
          </Button>

          {request.status?.toLowerCase() === 'draft' && onContinueDraft && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onContinueDraft(request)}
              iconName="Edit"
              iconPosition="left"
            >
              Continue
            </Button>
          )}

          {(isAdmin || isCoordinator) && request.status?.toLowerCase() === 'submitted' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onApprove(request)}
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                iconName="CheckCircle"
                iconPosition="left"
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReject(request)}
                className="text-red-600 border-red-300 hover:bg-red-50"
                iconName="XCircle"
                iconPosition="left"
              >
                Reject
              </Button>
            </>
          )}

          {/* Vendor Invitation - Coordinator Only */}
          {isCoordinator && request.status === 'approved' && (
            inviteState === INVITE_STATE.REGISTERED ? (
              <RegisteredBadge />
            ) : inviteState === INVITE_STATE.EXPIRED ? (
              // The link is dead, so offer the fix rather than a dead-end badge.
              // POST /:id/invite-vendor already mints a fresh invite when the
              // existing one is used or past expiry.
              <div className="flex items-center gap-2">
                <InviteExpiredBadge />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onInviteVendor(request)}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Re-invite
                </Button>
              </div>
            ) : inviteState === INVITE_STATE.INVITED ? (
              <InvitedBadge />
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onInviteVendor(request)}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                iconName="Mail"
                iconPosition="left"
              >
                Invite Vendor
              </Button>
            )
          )}
          
          {/* Admin View - Show Status Only */}
          {isAdmin && !isCoordinator && request.status === 'approved' && request.vendor_invited && (
            inviteState === INVITE_STATE.REGISTERED ? (
              <RegisteredBadge />
            ) : inviteState === INVITE_STATE.EXPIRED ? (
              <InviteExpiredBadge />
            ) : (
              <InvitedBadge />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestCard;

